import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, collection, getDocs, setDoc, deleteDoc } from 'firebase/firestore';
import { useOrganization } from '../contexts/OrganizationContext';
import { supabase } from '../supabase';

const SurgeryInfoModal = ({ surgery, onClose, onUpdate }) => {
  const { currentOrganization, currentUser } = useOrganization();
  const [tags, setTags] = useState(surgery?.tags || []);
  const [availableTags, setAvailableTags] = useState([]);
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [editingTag, setEditingTag] = useState(null); // 🆕 編集中のタグ
  const [hoveredTagId, setHoveredTagId] = useState(null); // 🆕 ホバー中のタグID
  const [newTag, setNewTag] = useState({
    name: '',
    category: '臨床情報',
    color: 'blue'
  });
  const [loading, setLoading] = useState(false);

  // === 配置担当者向けコメント (Phase E-4) ===
  const [callerStaffId, setCallerStaffId] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newCommentContent, setNewCommentContent] = useState('');
  const [selectedTargetIds, setSelectedTargetIds] = useState([]);
  const [postingComment, setPostingComment] = useState(false);

  const isAdmin = currentUser?.user_metadata?.role === 'admin';

  // localStorage から自分の staff.id を取得
  useEffect(() => {
    try {
      const raw = localStorage.getItem('supabase_user');
      if (!raw) return;
      const u = JSON.parse(raw);
      if (u?.id) setCallerStaffId(String(u.id));
    } catch (_) {
      // ignore
    }
  }, []);

  // この手術に配置されているスタッフ一覧（scrub + circulating を統合・重複除去）
  const assignedStaff = useMemo(() => {
    const toArr = (v) => (Array.isArray(v) ? v : v ? [v] : []);
    const buckets = [
      { role: '器械出し', list: toArr(surgery?.scrub) },
      { role: '外回り',   list: toArr(surgery?.circulating) },
    ];
    const map = new Map();
    buckets.forEach(({ role, list }) => {
      list.forEach((s) => {
        if (!s) return;
        const id = String(s.id || s.staffId || s.employeeId || '');
        if (!id) return;
        const name =
          s.name
            || [s.lastName, s.firstName].filter(Boolean).join(' ')
            || [s.last_name, s.first_name].filter(Boolean).join(' ')
            || id;
        if (map.has(id)) {
          map.get(id).roles.add(role);
        } else {
          map.set(id, { id, name, roles: new Set([role]) });
        }
      });
    });
    return Array.from(map.values()).map((s) => ({
      ...s,
      roles: Array.from(s.roles),
    }));
  }, [surgery]);

  // コメント取得
  const loadComments = useCallback(async () => {
    const surgeryId = surgery?.docId || surgery?.id;
    if (!currentOrganization?.id || !surgeryId) return;
    setCommentsLoading(true);
    try {
      const { data, error } = await supabase
        .from('surgery_staff_comments')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .eq('surgery_id', surgeryId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      setComments(data || []);
    } catch (e) {
      console.error('コメント取得失敗:', e);
    } finally {
      setCommentsLoading(false);
    }
  }, [currentOrganization?.id, surgery?.docId, surgery?.id]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const toggleTarget = (id) => {
    setSelectedTargetIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handlePostComment = async () => {
    const surgeryId = surgery?.docId || surgery?.id;
    if (!currentOrganization?.id || !surgeryId) return;
    if (!newCommentContent.trim()) {
      alert('コメント内容を入力してください');
      return;
    }
    if (selectedTargetIds.length === 0) {
      alert('宛先（誰宛のコメントか）を1名以上選択してください');
      return;
    }
    setPostingComment(true);
    try {
      const surgeryDate =
        surgery?.date
          || surgery?.surgery_date
          || new Date().toISOString().slice(0, 10);
      const authorName =
        currentUser?.user_metadata?.full_name
          || currentUser?.email
          || '管理者';
      const { error } = await supabase
        .from('surgery_staff_comments')
        .insert({
          organization_id: currentOrganization.id,
          surgery_id: surgeryId,
          surgery_date: surgeryDate,
          target_staff_ids: selectedTargetIds,
          author_staff_id: callerStaffId || 'unknown',
          author_name: authorName,
          content: newCommentContent.trim(),
        });
      if (error) throw error;
      setNewCommentContent('');
      setSelectedTargetIds([]);
      await loadComments();
    } catch (e) {
      console.error('コメント投稿失敗:', e);
      alert(`コメントの投稿に失敗しました: ${e.message}`);
    } finally {
      setPostingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('このコメントを削除しますか？')) return;
    try {
      const { error } = await supabase
        .from('surgery_staff_comments')
        .delete()
        .eq('id', commentId)
        .eq('organization_id', currentOrganization.id);
      if (error) throw error;
      await loadComments();
    } catch (e) {
      console.error('コメント削除失敗:', e);
      alert(`コメントの削除に失敗しました: ${e.message}`);
    }
  };

  const staffNameById = useMemo(() => {
    const m = {};
    assignedStaff.forEach((s) => { m[s.id] = s.name; });
    return m;
  }, [assignedStaff]);

  // タグカテゴリの定義
  const categories = [
    '臨床情報',
    '患者情報',
    '教育',
    'ロジスティクス',
    'リスク管理'
  ];

  // タグカラーの定義
  const colors = {
    blue: { bg: '#e3f2fd', text: '#1976d2' },
    red: { bg: '#ffebee', text: '#c62828' },
    green: { bg: '#e8f5e9', text: '#2e7d32' },
    orange: { bg: '#fff3e0', text: '#ef6c00' },
    purple: { bg: '#f3e5f5', text: '#7b1fa2' },
    pink: { bg: '#fce4ec', text: '#c2185b' },
    teal: { bg: '#e0f2f1', text: '#00695c' },
    amber: { bg: '#fff8e1', text: '#f57f17' },
  };

  // 既存タグを取得
  useEffect(() => {
    const fetchTags = async () => {
      if (!currentOrganization?.id) return;

      try {
        const tagsSnapshot = await getDocs(
          collection(db, 'organizations', currentOrganization.id, 'surgeryTags')
        );
        const tagsData = tagsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAvailableTags(tagsData);
      } catch (error) {
        console.error('タグ取得エラー:', error);
      }
    };

    fetchTags();
  }, [currentOrganization]);

  // タグを追加
  const handleAddTag = async (tag) => {
    if (tags.some(t => t.id === tag.id)) {
      alert('このタグは既に追加されています');
      return;
    }

    const newTags = [...tags, tag];
    setTags(newTags);
    await saveTags(newTags);
  };

  // タグを削除（手術から削除）
  const handleRemoveTag = async (tagId) => {
    const newTags = tags.filter(t => t.id !== tagId);
    setTags(newTags);
    await saveTags(newTags);
  };

  // タグを保存（surgeryRequests と surgerySchedules の両方に保存）
  const saveTags = async (newTags) => {
    // ✅ 修正: surgery.docId または surgery.id のどちらかが存在すればOK
    if (!currentOrganization?.id || (!surgery?.id && !surgery?.docId)) {
      console.error('❌ organizationId または surgeryId が未設定');
      return;
    }

    console.log('🏷️ タグ保存開始:', {
      surgeryId: surgery.id,
      surgeryDocId: surgery.docId,
      newTags
    });

    const orgId = currentOrganization.id;
    // ✅ 修正: docId を優先的に使用
    const requestDocId = surgery.docId || surgery.id;

    try {
      // 1️⃣ surgerySchedules に保存（人員配置モードで表示するため）
      if (surgery.docId) {
        const surgeryScheduleRef = doc(
          db,
          'organizations',
          orgId,
          'surgerySchedules',
          surgery.docId
        );

        // ✅ 修正: updateDoc → setDoc に変更（ドキュメントが存在しない場合に対応）
        await setDoc(surgeryScheduleRef, {
          tags: newTags,
          updatedAt: new Date()
        }, { merge: true });

        console.log('✅ surgerySchedules に保存完了');
      } else {
        console.warn('⚠️ surgery.docId が未設定のため surgerySchedules への保存をスキップ');
      }

      // 2️⃣ surgeryRequests に保存（手術申し込みデータに保存）
      const surgeryRequestRef = doc(
        db,
        'organizations',
        orgId,
        'surgeryRequests',
        requestDocId
      );

      // ✅ 修正: updateDoc → setDoc に変更（ドキュメントが存在しない場合に対応）
      await setDoc(surgeryRequestRef, {
        tags: newTags,
        updatedAt: new Date()
      }, { merge: true });

      console.log('✅ surgeryRequests に保存完了');
      console.log('🎉 タグ保存完了');

      if (onUpdate) {
        onUpdate({ ...surgery, tags: newTags });
      }
    } catch (error) {
      console.error('❌ タグ保存エラー:', error);
      alert(`タグの保存に失敗しました: ${error.message}`);
    }
  };

  // 🆕 タグを編集（マスタ更新）
  const handleEditTag = (tag) => {
    setEditingTag({ ...tag });
  };

  // 🆕 タグを更新
  const handleUpdateTag = async () => {
    if (!editingTag?.name.trim()) {
      alert('タグ名を入力してください');
      return;
    }

    setLoading(true);
    try {
      const tagRef = doc(
        db,
        'organizations',
        currentOrganization.id,
        'surgeryTags',
        editingTag.id
      );

      const updatedData = {
        name: editingTag.name.trim(),
        category: editingTag.category,
        color: editingTag.color,
        updatedAt: new Date()
      };

      await updateDoc(tagRef, updatedData);

      // ローカルステートを更新
      setAvailableTags(prev =>
        prev.map(t => (t.id === editingTag.id ? { ...t, ...updatedData } : t))
      );

      // 現在の手術に適用されているタグも更新
      setTags(prev =>
        prev.map(t => (t.id === editingTag.id ? { ...t, ...updatedData } : t))
      );

      setEditingTag(null);
      alert('タグを更新しました');
    } catch (error) {
      console.error('タグ更新エラー:', error);
      alert('タグの更新に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 🆕 タグを削除（マスタから完全削除）
  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('このタグをマスタから削除しますか？すべての手術から削除されます。')) {
      return;
    }

    setLoading(true);
    try {
      const tagRef = doc(
        db,
        'organizations',
        currentOrganization.id,
        'surgeryTags',
        tagId
      );

      await deleteDoc(tagRef);

      // ローカルステートから削除
      setAvailableTags(prev => prev.filter(t => t.id !== tagId));
      
      // 現在の手術からも削除
      if (tags.some(t => t.id === tagId)) {
        const newTags = tags.filter(t => t.id !== tagId);
        setTags(newTags);
        await saveTags(newTags);
      }

      alert('タグを削除しました');
    } catch (error) {
      console.error('タグ削除エラー:', error);
      alert('タグの削除に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 新規タグを作成
  const handleCreateTag = async () => {
    if (!newTag.name.trim()) {
      alert('タグ名を入力してください');
      return;
    }

    setLoading(true);
    try {
      const tagRef = doc(
        collection(db, 'organizations', currentOrganization.id, 'surgeryTags')
      );

      const tagData = {
        name: newTag.name.trim(),
        category: newTag.category,
        color: newTag.color,
        organizationId: currentOrganization.id,
        createdAt: new Date()
      };

      await setDoc(tagRef, tagData);

      // 作成したタグを追加
      const createdTag = { id: tagRef.id, ...tagData };
      setAvailableTags(prev => [...prev, createdTag]);
      await handleAddTag(createdTag);

      // フォームをリセット
      setNewTag({ name: '', category: '臨床情報', color: 'blue' });
      setIsCreatingTag(false);
      alert('タグを作成しました');
    } catch (error) {
      console.error('タグ作成エラー:', error);
      alert('タグの作成に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // カテゴリ別にタグをグループ化
  const groupedTags = categories.reduce((acc, category) => {
    acc[category] = availableTags.filter(tag => tag.category === category);
    return acc;
  }, {});

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>📋 手術情報</h3>
          <button onClick={onClose} style={styles.closeButton}>×</button>
        </div>

        <div style={styles.surgeryInfo}>
          <div style={styles.surgeryName}>{surgery?.surgeryName || '手術名不明'}</div>
          <div style={styles.surgeryTime}>
            {surgery?.startTime} - {surgery?.endTime}
          </div>
        </div>

        <div style={styles.content}>
          {/* === 配置担当者向けコメント (Phase E-4) === */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>💬 配置担当者へのコメント</div>

            {/* コメント一覧 */}
            <div style={styles.commentList}>
              {commentsLoading ? (
                <div style={styles.noTags}>読み込み中...</div>
              ) : comments.length === 0 ? (
                <div style={styles.noTags}>まだコメントはありません</div>
              ) : (
                comments.map((c) => {
                  const targetIds = Array.isArray(c.target_staff_ids)
                    ? c.target_staff_ids
                    : [];
                  const targetLabels = targetIds.map(
                    (id) => staffNameById[String(id)] || `(ID:${id})`
                  );
                  return (
                    <div key={c.id} style={styles.commentItem}>
                      <div style={styles.commentMeta}>
                        <span style={styles.commentAuthor}>
                          {c.author_name || '（投稿者不明）'}
                        </span>
                        <span style={styles.commentDate}>
                          {c.created_at
                            ? new Date(c.created_at).toLocaleString('ja-JP')
                            : ''}
                        </span>
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteComment(c.id)}
                            style={styles.commentDeleteBtn}
                            title="削除"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                      <div style={styles.commentTargets}>
                        宛先: {targetLabels.length > 0 ? targetLabels.join('、') : '—'}
                      </div>
                      <div style={styles.commentBody}>{c.content}</div>
                    </div>
                  );
                })
              )}
            </div>

            {/* 投稿フォーム（admin のみ） */}
            {isAdmin && (
              <div style={styles.commentForm}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    宛先（配置されている人から複数選択可）
                  </label>
                  {assignedStaff.length === 0 ? (
                    <div style={styles.noTags}>
                      この手術にはまだ誰も配置されていません
                    </div>
                  ) : (
                    <div style={styles.targetPicker}>
                      {assignedStaff.map((s) => {
                        const checked = selectedTargetIds.includes(s.id);
                        return (
                          <label
                            key={s.id}
                            style={{
                              ...styles.targetChip,
                              backgroundColor: checked ? '#1976d2' : '#f0f0f0',
                              color: checked ? '#fff' : '#333',
                              borderColor: checked ? '#1976d2' : '#ddd',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleTarget(s.id)}
                              style={{ display: 'none' }}
                            />
                            <span>{s.name}</span>
                            <span style={styles.targetRole}>
                              ({s.roles.join('/')})
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>コメント内容</label>
                  <textarea
                    value={newCommentContent}
                    onChange={(e) => setNewCommentContent(e.target.value)}
                    placeholder="例: 入室時、患者さんの○○に注意してください"
                    rows={3}
                    style={{
                      ...styles.input,
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>

                <div style={styles.buttonGroup}>
                  <button
                    onClick={handlePostComment}
                    disabled={postingComment}
                    style={styles.saveButton}
                  >
                    {postingComment ? '投稿中...' : 'コメントを投稿'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 現在のタグ */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>現在のタグ</div>
            <div style={styles.tagContainer}>
              {tags.length === 0 ? (
                <div style={styles.noTags}>タグが設定されていません</div>
              ) : (
                tags.map(tag => (
                  <div
                    key={tag.id}
                    style={{
                      ...styles.tag,
                      backgroundColor: colors[tag.color]?.bg || colors.blue.bg,
                      color: colors[tag.color]?.text || colors.blue.text,
                    }}
                  >
                    <span>{tag.name}</span>
                    <button
                      onClick={() => handleRemoveTag(tag.id)}
                      style={styles.removeButton}
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* タグ追加セクション */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>タグを追加</div>
            
            {/* カテゴリ別タグ表示 */}
            {categories.map(category => {
              const categoryTags = groupedTags[category] || [];
              if (categoryTags.length === 0) return null;

              return (
                <div key={category} style={styles.categorySection}>
                  <div style={styles.categoryTitle}>{category}</div>
                  <div style={styles.tagContainer}>
                    {categoryTags.map(tag => (
                      <div
                        key={tag.id}
                        style={{
                          position: 'relative',
                          display: 'inline-block'
                        }}
                        onMouseEnter={() => setHoveredTagId(tag.id)}
                        onMouseLeave={() => setHoveredTagId(null)}
                      >
                        <button
                          onClick={() => handleAddTag(tag)}
                          disabled={tags.some(t => t.id === tag.id)}
                          style={{
                            ...styles.addableTag,
                            backgroundColor: colors[tag.color]?.bg || colors.blue.bg,
                            color: colors[tag.color]?.text || colors.blue.text,
                            opacity: tags.some(t => t.id === tag.id) ? 0.5 : 1,
                            cursor: tags.some(t => t.id === tag.id) ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {tag.name}
                        </button>
                        
                        {/* 🆕 ホバー時の編集・削除ボタン */}
                        {hoveredTagId === tag.id && (
                          <div style={styles.hoverButtons}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditTag(tag);
                              }}
                              style={styles.editButton}
                              title="編集"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTag(tag.id);
                              }}
                              style={styles.deleteButton}
                              title="削除"
                            >
                              🗑️
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* 新規タグ作成ボタン */}
            <button
              onClick={() => setIsCreatingTag(true)}
              style={styles.createButton}
            >
              + 新しいタグを作成
            </button>
          </div>

          {/* 🆕 タグ編集フォーム */}
          {editingTag && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>タグを編集</div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>タグ名</label>
                <input
                  type="text"
                  value={editingTag.name}
                  onChange={(e) => setEditingTag({ ...editingTag, name: e.target.value })}
                  style={styles.input}
                  placeholder="例: 教育症例"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>カテゴリ</label>
                <select
                  value={editingTag.category}
                  onChange={(e) => setEditingTag({ ...editingTag, category: e.target.value })}
                  style={styles.select}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>色</label>
                <div style={styles.colorPicker}>
                  {Object.keys(colors).map(colorKey => (
                    <div
                      key={colorKey}
                      onClick={() => setEditingTag({ ...editingTag, color: colorKey })}
                      style={{
                        ...styles.colorButton,
                        backgroundColor: colors[colorKey].bg,
                        border: editingTag.color === colorKey ? `3px solid ${colors[colorKey].text}` : '2px solid transparent',
                        transform: editingTag.color === colorKey ? 'scale(1.1)' : 'scale(1)',
                      }}
                    />
                  ))}
                </div>
              </div>

              <div style={styles.buttonGroup}>
                <button
                  onClick={() => setEditingTag(null)}
                  style={styles.cancelButton}
                  disabled={loading}
                >
                  キャンセル
                </button>
                <button
                  onClick={handleUpdateTag}
                  style={styles.saveButton}
                  disabled={loading}
                >
                  {loading ? '更新中...' : '更新'}
                </button>
              </div>
            </div>
          )}

          {/* 新規タグ作成フォーム */}
          {isCreatingTag && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>新しいタグを作成</div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>タグ名</label>
                <input
                  type="text"
                  value={newTag.name}
                  onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                  style={styles.input}
                  placeholder="例: 教育症例"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>カテゴリ</label>
                <select
                  value={newTag.category}
                  onChange={(e) => setNewTag({ ...newTag, category: e.target.value })}
                  style={styles.select}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>色</label>
                <div style={styles.colorPicker}>
                  {Object.keys(colors).map(colorKey => (
                    <div
                      key={colorKey}
                      onClick={() => setNewTag({ ...newTag, color: colorKey })}
                      style={{
                        ...styles.colorButton,
                        backgroundColor: colors[colorKey].bg,
                        border: newTag.color === colorKey ? `3px solid ${colors[colorKey].text}` : '2px solid transparent',
                        transform: newTag.color === colorKey ? 'scale(1.1)' : 'scale(1)',
                      }}
                    />
                  ))}
                </div>
              </div>

              <div style={styles.buttonGroup}>
                <button
                  onClick={() => {
                    setIsCreatingTag(false);
                    setNewTag({ name: '', category: '臨床情報', color: 'blue' });
                  }}
                  style={styles.cancelButton}
                  disabled={loading}
                >
                  キャンセル
                </button>
                <button
                  onClick={handleCreateTag}
                  style={styles.saveButton}
                  disabled={loading}
                >
                  {loading ? '作成中...' : '作成'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// スタイル定義
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    maxWidth: '800px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e0e0e0',
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 1,
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#666',
    lineHeight: 1,
    padding: '0 8px',
  },
  surgeryInfo: {
    padding: '20px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0',
  },
  surgeryName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px',
  },
  surgeryTime: {
    fontSize: '14px',
    color: '#666',
  },
  content: {
    padding: '24px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '12px',
  },
  tagContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  tag: {
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  removeButton: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
    opacity: 0.7,
  },
  noTags: {
    color: '#999',
    fontSize: '14px',
    fontStyle: 'italic',
  },
  categorySection: {
    marginBottom: '16px',
  },
  categoryTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#666',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  addableTag: {
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    border: 'none',
    transition: 'all 0.2s',
  },
  hoverButtons: {
    position: 'absolute',
    top: '-10px',
    right: '-10px',
    display: 'flex',
    gap: '4px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 10,
  },
  editButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '4px',
  },
  deleteButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '4px',
  },
  createButton: {
    marginTop: '12px',
    padding: '10px 20px',
    backgroundColor: '#f0f0f0',
    border: '2px dashed #ccc',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#666',
    width: '100%',
    transition: 'all 0.2s',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    color: '#666',
    marginBottom: '6px',
    fontWeight: '500',
  },
  input: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  colorPicker: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  colorButton: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '16px',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#f0f0f0',
    border: '1px solid #ccc',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  // === コメント機能 (Phase E-4) ===
  commentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px',
  },
  commentItem: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '12px 14px',
  },
  commentMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
    fontSize: '12px',
    color: '#666',
  },
  commentAuthor: {
    fontWeight: 600,
    color: '#333',
  },
  commentDate: {
    color: '#999',
  },
  commentDeleteBtn: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '2px 6px',
  },
  commentTargets: {
    fontSize: '12px',
    color: '#1976d2',
    marginBottom: '6px',
  },
  commentBody: {
    fontSize: '14px',
    color: '#333',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.5,
  },
  commentForm: {
    backgroundColor: '#fafafa',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '14px',
  },
  targetPicker: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  targetChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '16px',
    fontSize: '13px',
    border: '1px solid',
    cursor: 'pointer',
    userSelect: 'none',
  },
  targetRole: {
    fontSize: '11px',
    opacity: 0.8,
  },
};

export default SurgeryInfoModal;